import { useState, useRef, useCallback, useEffect } from 'react';
import { Animated } from 'react-native';

export interface UseHoldToConfirmOptions {
    /** How long the press must be held to confirm (ms). Default 1200. */
    holdMs?: number;
    /** Called once when the hold threshold is reached. */
    onConfirm?: () => void;
    /** Called when the hold starts (press-in). */
    onStart?: () => void;
    /** Called when the hold is cancelled (early release or external cancel). */
    onCancel?: () => void;
}

export interface UseHoldToConfirmReturn {
    /** Animated.Value 0→1 representing hold progress. Attach to a fill bar/ring. */
    progress: Animated.Value;
    /** True while the user is actively holding (press-in → threshold or release). */
    isConfirming: boolean;
    /** Attach to the touchable's onPressIn. */
    pressIn: () => void;
    /** Attach to the touchable's onPressOut. Cancels if threshold not reached. */
    pressOut: () => void;
    /** Programmatically cancel an in-flight hold. */
    cancel: () => void;
}

/**
 * Press-and-hold-to-confirm pattern for young children.
 *
 * A quick tap does nothing — the child must press and hold for `holdMs`
 * (default 1.2s) to trigger `onConfirm`. While held, `progress` animates
 * 0→1 so the UI can show a fill bar/ring + "Keep holding…" text. Releasing
 * before the threshold cancels and resets.
 *
 * Mirrors the conventions of `useTwoTapConfirm` (same `src/hooks/` folder,
 * same timeout/cancel pattern). Uses native `onPressIn`/`onPressOut` +
 * `Animated.timing` — no extra dependencies.
 */
export function useHoldToConfirm(options: UseHoldToConfirmOptions = {}): UseHoldToConfirmReturn {
    const { holdMs = 1200, onConfirm, onStart, onCancel } = options;
    const [isConfirming, setIsConfirming] = useState(false);
    const progress = useRef(new Animated.Value(0)).current;
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const completedRef = useRef(false);

    const clearTimer = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const resetProgress = useCallback(() => {
        progress.stopAnimation(() => {
            progress.setValue(0);
        });
    }, [progress]);

    const cancel = useCallback(() => {
        clearTimer();
        if (isConfirming && !completedRef.current) {
            setIsConfirming(false);
            resetProgress();
            onCancel?.();
        }
    }, [isConfirming, clearTimer, resetProgress, onCancel]);

    const pressIn = useCallback(() => {
        completedRef.current = false;
        setIsConfirming(true);
        onStart?.();
        // Animate progress 0→1 over holdMs.
        Animated.timing(progress, {
            toValue: 1,
            duration: holdMs,
            useNativeDriver: false,
        }).start();
        // Fire onConfirm after the threshold.
        clearTimer();
        timerRef.current = setTimeout(() => {
            completedRef.current = true;
            setIsConfirming(false);
            // Snap progress to 1 then reset so the next hold starts clean.
            progress.setValue(1);
            resetProgress();
            onConfirm?.();
        }, holdMs);
    }, [holdMs, onStart, onConfirm, progress, clearTimer, resetProgress]);

    const pressOut = useCallback(() => {
        if (completedRef.current) return; // Hold already completed — let it fire.
        clearTimer();
        setIsConfirming(false);
        resetProgress();
        onCancel?.();
    }, [clearTimer, resetProgress, onCancel]);

    // Cleanup on unmount.
    useEffect(() => {
        return () => {
            clearTimer();
        };
    }, [clearTimer]);

    return { progress, isConfirming, pressIn, pressOut, cancel };
}
